import React, { useRef } from 'react';

import { cn } from '../../lib/utils';

/**
 * Auswahl aus wenigen gleichrangigen Moeglichkeiten - Prioritaet, Zeitraum,
 * Zustandsfilter, Kachel/Liste.
 *
 * Am 2026-08-09 habe ich diesen Stellen `role="radio"` und `aria-checked`
 * gegeben, ohne die dazugehoerige Bedienung zu bauen: Jede Schaltflaeche blieb
 * ein eigener Tabstopp, die Pfeiltasten taten nichts. **Das ist schlechter als
 * gewoehnliche Knoepfe**, weil es ein Verhalten ankuendigt, das es nicht gibt -
 * wer die Ankuendigung hoert und die Pfeiltaste drueckt, steht vor einer
 * Gruppe, die sich nicht bewegt. (Codex-Durchsicht zu #45, #46, #47.)
 *
 * Hier ist es vollstaendig:
 *
 * - **Ein Tabstopp** fuer die ganze Gruppe (`tabIndex` wandert mit der Wahl).
 * - **Pfeiltasten** waehlen die Nachbarn, `Home`/`End` die Enden.
 * - Die Wahl folgt dem Fokus, wie es fuer eine Radiogruppe ueblich ist.
 */

export interface RadioOption<T extends string> {
  id: T;
  label: string;
  /** Erklaerung als `title`, wenn die Beschriftung allein zu knapp ist. */
  title?: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface RadioGroupProps<T extends string> {
  /** Wofuer die Auswahl gilt - fuer Vorleseprogramme. */
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: ReadonlyArray<RadioOption<T>>;
  className?: string;
  /** Klassen je Schaltflaeche; die Gestaltung bleibt bei der Seite. */
  optionClassName: (option: RadioOption<T>, aktiv: boolean) => string;
  /** Ersetzt die Beschriftung, etwa um eine Anzahl daneben zu setzen. */
  renderLabel?: (option: RadioOption<T>, aktiv: boolean) => React.ReactNode;
}

export function RadioGroup<T extends string>({
  label, value, onChange, options, className, optionClassName, renderLabel,
}: RadioGroupProps<T>) {
  const gruppe = useRef<HTMLDivElement>(null);

  const waehle = (index: number) => {
    const ziel = options[(index + options.length) % options.length];
    onChange(ziel.id);
    // Der Fokus folgt der Wahl - sonst zeigt die Tastatur auf ein anderes
    // Element als die Auswahl.
    const knoepfe = gruppe.current?.querySelectorAll<HTMLElement>('[role="radio"]');
    knoepfe?.[(index + options.length) % options.length]?.focus();
  };

  const beiTaste = (event: React.KeyboardEvent, index: number) => {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        waehle(index + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        waehle(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        waehle(0);
        break;
      case 'End':
        event.preventDefault();
        waehle(options.length - 1);
        break;
      default:
        break;
    }
  };

  // Ist nichts gewaehlt, traegt die erste Moeglichkeit den Tabstopp - sonst
  // waere die Gruppe per Tastatur gar nicht erreichbar.
  const tabstoppIndex = Math.max(0, options.findIndex((o) => o.id === value));

  return (
    <div ref={gruppe} role="radiogroup" aria-label={label} className={cn('flex flex-wrap gap-2', className)}>
      {options.map((option, index) => {
        const aktiv = option.id === value;
        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={aktiv}
            tabIndex={index === tabstoppIndex ? 0 : -1}
            title={option.title}
            onClick={() => onChange(option.id)}
            onKeyDown={(event) => beiTaste(event, index)}
            className={cn(
              'transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
              optionClassName(option, aktiv),
            )}
          >
            {option.icon && <option.icon className="h-4 w-4 shrink-0" />}
            {renderLabel ? renderLabel(option, aktiv) : option.label}
          </button>
        );
      })}
    </div>
  );
}

export default RadioGroup;
